import { Container } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card'
import type { CaseDetail } from '@/types/domain'

/** Container and seal pairs, with the equipment summed in the footer. The type
 *  moved off the rows and into "40' HC × 2" — on a case every box is usually
 *  the same type, and repeating it per row buries the numbers that differ.
 *
 *  Real rows as of Story 3.4 (FR-67): this is the union of what each LEG
 *  carries, composed server-side, and it used to render a permanently-empty
 *  placeholder. Two consequences the card has to handle now that the data
 *  exists — a box may appear on two legs (it genuinely was on two, and its seal
 *  may differ between them), and a box's type may be absent because nobody
 *  recorded it. */
export function ContainersCard({ containers }: { containers: CaseDetail['containers'] }) {
  const { t } = useTranslation()

  /* Only the boxes whose type somebody recorded are summed. An untyped box is
     NOT counted as its own "" × 3 bucket, which is what an unguarded tally
     would produce — and an equipment line reading " × 3" is worse than one
     that simply omits what nobody stated. */
  const counts = new Map<string, number>()
  for (const container of containers) {
    if (!container.type) continue
    counts.set(container.type, (counts.get(container.type) ?? 0) + 1)
  }
  const equipment = [...counts].map(([type, count]) => `${type} × ${count}`).join(' · ')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Container size={15} className="text-text-muted" aria-hidden />
          {t('cases.overview.containers')}
        </CardTitle>
      </CardHeader>
      {containers.length === 0 ? (
        <EmptyState
          title={t('cases.overview.containersEmptyTitle')}
          description={t('cases.overview.containersEmptyDescription')}
          className="py-6"
        />
      ) : (
        <>
          <CardBody className="pt-2 pb-2">
            <ul className="flex flex-col">
              {containers.map((container) => (
                <li
                  /* Keyed on the ROW id, not the container number. The same box
                     legitimately appears on two legs — de-stuffed at the border
                     and re-stuffed for the run inland — and a number key would
                     collapse the two into one, hiding the leg the reader is
                     looking for. */
                  key={container.id}
                  className="border-line-hair flex flex-wrap items-center gap-x-3 gap-y-1 border-b py-2.5 last:border-b-0"
                >
                  {/* Containers and seals are monospace — references compared
                      character by character, not words read. Each wraps as a
                      unit, never inside itself: a container number split across
                      lines can no longer be checked against a bill of lading. */}
                  <span className="text-text-heading font-mono text-[12.5px] whitespace-nowrap">
                    {container.number}
                  </span>
                  {/* Which movement carried it. Two rows of container numbers
                      mean nothing on a transhipped case until you know one
                      crossed the water and the other finished the run. */}
                  <span className="text-text-muted text-[12px] font-light">
                    {t('cases.legs.legN', { n: container.legSequence })}
                    {container.legRoute ? ` · ${container.legRoute}` : null}
                  </span>
                  <span className="flex-1" />
                  {container.sealNumber ? (
                    <span className="text-text-muted font-mono text-[12px] whitespace-nowrap">
                      {t('cases.overview.seal')} {container.sealNumber}
                    </span>
                  ) : (
                    <span className="text-text-muted text-[12px] font-light whitespace-nowrap">
                      {t('cases.legs.sealNotRecorded')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
          {/* Absent rather than empty when no box carries a type. A footer
              reading "Equipment" beside nothing states that the equipment is
              known and is nothing, which is neither true nor useful — where an
              absent footer says only that nobody recorded it. */}
          {equipment ? (
            <CardFooter className="justify-between">
              <span className="asa-eyebrow">{t('cases.overview.equipment')}</span>
              <span className="text-text-body text-[12.5px]">{equipment}</span>
            </CardFooter>
          ) : null}
        </>
      )}
    </Card>
  )
}
